import { Component, computed, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
    FormGroup,
    UntypedFormBuilder,
    UntypedFormGroup,
} from '@angular/forms';
import {
    configurationPropertyMachines,
    configurationPropertyMeters,
    configurationPropertyModBusOption,
    PossiblesCompileMachines,
    PossiblesCompileMachinesKeys,
    PossiblesCompileMeters
} from '../../../configurationProperty/IoTDeviceConfigurationConfiguration';
import { htmlData, htmlFormElement } from '../../../pages/management-elements/elements-page/elements-page.component';
import {
    arrayIsSet,
    KeyStringValue,
    objectIsSet,
    stringIsSet
} from '../../../providers/service/global-variables.service';
import { IotDevicesParse } from '../../../ParseClass/Iotdevices.Parse';
import {
    configurationElement,
    getElementByKeyName,
    getPossibleValue,
    getValidator,
    getValueByElement,
    isPointerConfigurationElement,
    typeFormValue
} from '../../../configurationProperty/configurationPropertyUtils';
import { dataForm } from '../../../widget/auto-complete-form/auto-complete-form.component';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { filter, finalize, map, pairwise, startWith, switchMap } from "rxjs/operators";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
    BigBuddyPinWrapper,
    IoTDeviceConfigurationObject,
    IoTDeviceConfigurationParse2,
    MachineConfiguration,
    MeterConfiguration,
    PicoBuddyPinWrapper
} from "../../../ParseClass/IoTDeviceConfiguration.Parse";
import { ElementParse, ElementParse_v2 } from "../../../ParseClass/Element.Parse";
import { ElementTypeCategoryEnum } from "../../../ParseClass/ElementType.Parse";
import { SensorModelParse } from "../../../ParseClass/SensorModel.Parse";
import { SensorDataTypesParse } from "../../../ParseClass/sensor-data-types-parse";
import { SpecialButton } from "../../../widget/key-values-field/key-values-field.component";
import { PossibleValuesFormField } from "../../../widget/multi-select-form-field/multi-select-form-field.component";
import { TranslateService } from "@ngx-translate/core";
import { OpenDialogService } from 'src/app/providers/service/open-dialog.service';
import { AlertService } from '../../../providers/service/alert.service';


export type keyForm = 'meters' | 'machines' | 'connectionOptions';
export type TitleMetersType = {
    baudRate?: number;
    mqttTopics?: string;
    model?: string;
    modbusId?: number;
    elementName?: string;
}
export type TitleMachinesType = {
    title?: string;
    otherValues?: { traduction?: string, value: any }[]
}
export type RowSummaryType = {
    name?: string;
    model?: string;
    protocolTraduction: string;
    svgIcon: string;
}
export type FormGroupHtmlType = {
    key: string;
    traduction: string;
    icon: string;
    fields: htmlFormElement[];
}
export type DeviceKey = 'meters' | 'machines';
export type EditableConfiguration = {
    machines: any[];
    meters: any[];
    connectionOptions: any;
}
export type SelectedDevice = { kind: keyForm, id?: string };
type DeviceInfo = {
    // value sent to setIoTDeviceConfigurationObject_v2 (same conversion as the old FormArray rows)
    payload: any;
    serialized: string;
    formRaw: any;
    invalidKeys: string[] | undefined;
    hasInvalidControl: boolean;
}
type DeviceError = { index: number, id: string, keys: string[] };

@Component({
    selector: 'app-update-iot-configuration',
    templateUrl: './update-iot-configuration.component.html',
    styleUrls: ['./update-iot-configuration.component.scss'],
    standalone: false
})
export class UpdateIotConfigurationComponent implements OnInit, OnDestroy {
    // the only form: filled with the selected device, every change is written into editedConfiguration
    editForm: UntypedFormGroup | undefined;
    private editFormSubscriptions: Subscription[] = [];
    private deviceInfoCache = new WeakMap<object, DeviceInfo>();
    private originalConfiguration = signal<EditableConfiguration>({ machines: [], meters: [], connectionOptions: {} });
    editedConfiguration = signal<EditableConfiguration>({ machines: [], meters: [], connectionOptions: {} });
    machines = computed(() => this.editedConfiguration().machines);
    meters = computed(() => this.editedConfiguration().meters);
    connectionOptions = computed(() => this.editedConfiguration().connectionOptions);
    connectionOptionsTitle = computed(() => this.getTitleConnectionOptionPredicate(this.connectionOptions()));
    selected = signal<SelectedDevice>({ kind: 'connectionOptions' });
    saving = signal(false);
    private originalSerialized = computed(() => {
        const original = this.originalConfiguration();
        const serialized = new Map<string, string>();
        (['machines', 'meters'] as DeviceKey[]).forEach(key => {
            original[key].forEach(item => serialized.set(item.id, this.getDeviceInfo(key, item).serialized));
        });
        serialized.set('connectionOptions', this.getDeviceInfo('connectionOptions', original.connectionOptions).serialized);
        return serialized;
    });
    // ids (or 'connectionOptions') whose value to send differs from the original one
    modifiedIds = computed(() => {
        const original = this.originalSerialized();
        const edited = this.editedConfiguration();
        const ids = new Set<string>();
        (['machines', 'meters'] as DeviceKey[]).forEach(key => {
            edited[key].forEach(item => {
                if (original.get(item.id) != this.getDeviceInfo(key, item).serialized) {
                    ids.add(item.id);
                }
            });
        });
        if (original.get('connectionOptions') != this.getDeviceInfo('connectionOptions', edited.connectionOptions).serialized) {
            ids.add('connectionOptions');
        }
        return ids;
    });
    errors = computed(() => {
        const edited = this.editedConfiguration();
        const errors: { [k in DeviceKey]: DeviceError[] } = { machines: [], meters: [] };
        (['machines', 'meters'] as DeviceKey[]).forEach(key => {
            edited[key].forEach((item, index) => {
                const info = this.getDeviceInfo(key, item);
                if (arrayIsSet(info.invalidKeys) && info.hasInvalidControl) {
                    errors[key].push({ index, id: item.id, keys: info.invalidKeys });
                }
            });
        });
        return errors;
    });
    private selectedOriginalFormValues = computed(() => {
        const selected = this.selected();
        const original = this.findDevice(this.originalConfiguration(), selected);
        return original != null ? this.getDeviceInfo(selected.kind, original).formRaw : undefined;
    });
    public htmlData: { [k in keyForm]: htmlData[] } = { meters: [], machines: [], connectionOptions: [] };
    possiblesCompileMachines = PossiblesCompileMachines;
    possiblesCompileMeters = PossiblesCompileMeters;
    public formObject: { [k in keyForm]: any | undefined } = {
        meters: undefined,
        machines: undefined,
        connectionOptions: undefined
    };
    public configurationProperties: { [k: string]: configurationElement[] } = {
        meters: configurationPropertyMeters,
        machines: configurationPropertyMachines,
        connectionOptions: configurationPropertyModBusOption
    };
    public notSavedKeyForm = {
        meters: undefined,
        machines: undefined,
        connectionOptions: undefined
    }


    sectionNumberName: { [k: string]: string } = { 0: 'features' };
    typeForm = typeFormValue;
    subscriptions: Subscription[] = [];

    possibleValuesPointer: { [k: string]: any[] }
    formFilter: FormGroup;
    enableChanges$: Observable<boolean>;
    enableChangesTraduction$: Observable<string>;
    public serialDevice: string | undefined;
    isEditor = false
    public possibleValuesSpecial;
    sensorDataType: SensorDataTypesParse[] | undefined
    currentLang$: Observable<string>

    // public connectionOptionsError$: Observable<number[] | undefined>;

    constructor(public dialogRef: MatDialogRef<UpdateIotConfigurationComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            iotDevice: IotDevicesParse,
            currentValue: IoTDeviceConfigurationParse2,
            // performed by the opener: the dialog closes only when it succeeds
            save: (configuration: IoTDeviceConfigurationObject) => Observable<unknown>,
            possibleValuesPointer: { [k: string]: any[] },
            possibleValuesSpecial: { [k: string]: { [T: string]: SensorModelParse[] } },
            sensorDataType: SensorDataTypesParse[],
            isEditor: boolean | undefined
        },
        private fb: UntypedFormBuilder,
        iconRegistry: MatIconRegistry,
        sanitizer: DomSanitizer,
        private ts: TranslateService,
        private openDialogService: OpenDialogService,
        private alertService: AlertService
    ) {
        this.currentLang$ = this.ts.onLangChange.asObservable().pipe(
            startWith(undefined),
            map(lang => this.ts.currentLang ?? 'it')
        )
        iconRegistry.addSvgIcon('duplicate', sanitizer.bypassSecurityTrustResourceUrl('assets/icon/button/duplicate.svg'));
        [...Object.values(ElementTypeCategoryEnum), 'UNCATEGORIZED'].forEach(category => {
            iconRegistry.addSvgIcon('element-category-' + category, sanitizer.bypassSecurityTrustResourceUrl(`assets/element_category/${category}.svg`));
        });
        this.composeForm(this.data.currentValue.configuration?.connectionOptions, 'connectionOptions');
        if (this.data && this.data.iotDevice && stringIsSet(this.data.iotDevice.seriale)) {
            this.serialDevice = this.data.iotDevice.seriale;
        }
        if (this.data.isEditor) {
            this.isEditor = this.data.isEditor
        }

        if (this.data.possibleValuesSpecial) {
            this.possibleValuesSpecial = this.data.possibleValuesSpecial
        }
        if (this.data.sensorDataType) {
            this.sensorDataType = this.data.sensorDataType
        }
        this.possibleValuesPointer = this.data.possibleValuesPointer

        this.formFilter = this.fb.group({ key: undefined, enableChanges: undefined, types: [['machines', 'meters']] })
        this.enableChanges$ = this.formFilter.get('enableChanges')!.valueChanges!.pipe(
            startWith(undefined),
            map(() => this.enableChanges)
        )
        this.enableChangesTraduction$ = this.enableChanges$.pipe(
            map((value) => value ? 'disableChanges' : 'enableChanges')
        )
        const keys: keyForm[] = ['machines', 'meters'];
        keys.forEach(key => {
            this.composeForm({}, key);

        });
        const original = this.toEditableConfiguration(this.data.currentValue?.configuration);
        this.originalConfiguration.set(original);
        this.editedConfiguration.set(this.cloneConfiguration(original));
    }

    getErrors(key: DeviceKey): DeviceError[] | undefined {
        const errors = this.errors()[key];
        return arrayIsSet(errors) ? errors : undefined;
    }

    hasErrors(key: DeviceKey): boolean {
        return arrayIsSet(this.errors()[key]);
    }

    deviceHasError(key: DeviceKey, id: string | undefined): boolean {
        return this.errors()[key].some(e => e.id == id);
    }

    getPossibleValuesPointerByKey(keyName: string): any[] {
        return this.possibleValuesPointer && arrayIsSet(this.possibleValuesPointer[keyName]) ? this.possibleValuesPointer[keyName] : [];
    }

    getTrackCompileOption(index: number, possible: { value: any }): string | number {
        return possible?.value ?? index;
    }

    getTrackHtmlTab(index: number, tab: htmlData): string {
        return `${tab?.tabGroupName ?? 'tab'}-${index}`;
    }

    getTrackHtmlForm(index: number, form: htmlFormElement): string {
        return form?.formControlName ?? `form-${index}`;
    }

    getTrackOtherValue(index: number, otherValue: { traduction?: string, value: any }): string {
        return `${otherValue?.traduction ?? 'value'}-${otherValue?.value ?? ''}-${index}`;
    }

    // configuration as received, each machine/meter with its id; missing comType inferred like the old addControls
    private toEditableConfiguration(configuration: IoTDeviceConfigurationObject | undefined): EditableConfiguration {
        const withComType = <T extends { comType?: any }>(key: DeviceKey, item: T): T => {
            if (item.comType == null) {
                const comType = this.inferComType(key, item);
                if (comType != null) {
                    item.comType = comType;
                }
            }
            return item;
        };
        return {
            machines: (configuration?.machines ?? []).map(m => withComType('machines', MachineConfiguration.fromJson(m))),
            meters: (configuration?.meters ?? []).map(m => withComType('meters', MeterConfiguration.fromJson(m))),
            connectionOptions: { ...(configuration?.connectionOptions ?? {}) },
        };
    }

    private cloneConfiguration(configuration: EditableConfiguration): EditableConfiguration {
        return {
            machines: configuration.machines.map(m => MachineConfiguration.fromJson(m)),
            meters: configuration.meters.map(m => MeterConfiguration.fromJson(m)),
            connectionOptions: { ...configuration.connectionOptions },
        };
    }

    // comType chosen by model or by the protocol with most filled holdKeys
    private inferComType(key: DeviceKey, item: any): number | undefined {
        const objForm = this.getRowFormObject(key);
        const possibleCompile = (key == "machines" ? this.possiblesCompileMachines : this.possiblesCompileMeters) as { value: number, holdKeys: string[] }[];
        const counter: any = possibleCompile.reduce((prev, current) => {
            prev[(current.value).toString()] = 0;
            return prev;
        }, {} as any)
        Object.keys(objForm).forEach(keyForm => {
            const value = getPossibleValue(keyForm, this.configurationProperties[key], item);
            if (value != null) {
                possibleCompile.forEach(pValue => {
                    if (pValue.holdKeys.includes(keyForm)) {
                        counter[pValue.value]++;
                    }
                });
            } else {
                possibleCompile.forEach(pValue => {
                    if (pValue.holdKeys.includes(keyForm) && arrayIsSet(objForm[keyForm][1])) {
                        counter[pValue.value]--;
                    }
                });
            }
        });
        const allCounterValues = Object.values(counter)
        const allIsEquals = new Set(allCounterValues).size == 1
        let maxValue = 0
        let maxIndex: number | null = null
        if (!allIsEquals) {
            allCounterValues.forEach((value: any, index) => {
                if (maxValue < value || maxIndex == null) {
                    maxValue = value;
                    maxIndex = index
                }
            })
        }
        const maxKey = maxIndex != null ? Object.keys(counter)[maxIndex] : undefined
        let comType = getPossibleValue('comType', this.configurationProperties[key], item);
        const modelId = getPossibleValue('model', this.configurationProperties[key], item);
        if (comType == null && this.data.possibleValuesSpecial?.[key]?.model && modelId) {
            const sensorModel = this.data.possibleValuesSpecial[key].model.find(m => m.modelId == modelId)
            if (sensorModel?.comType) {
                comType = sensorModel.comType
            }
        } else if (comType == null) {
            comType = maxKey == null ? PossiblesCompileMachinesKeys.VIRTUAL : parseInt(maxKey);
        }
        return comType ?? undefined;
    }

    ngOnInit(): void {
        this.selectFirstDevice();
    }

    private selectFirstDevice() {
        const machines = this.machines();
        const meters = this.meters();
        if (arrayIsSet(machines)) {
            this.selectRow('machines', machines[0].id);
        } else if (arrayIsSet(meters)) {
            this.selectRow('meters', meters[0].id);
        } else {
            this.selectRow('connectionOptions');
        }
    }

    ngOnDestroy(): void {
        this.editFormSubscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
    getErrorMessageDialog$(controlMachine: boolean, controlMeter: boolean): Observable<{ action: string }[] | undefined> {


        const errorsMeters = this.getErrors('meters')
        const errorsMachines = this.getErrors('machines')
        const messages = [

        ]


        if (arrayIsSet(errorsMeters) && controlMeter) {
            const subTitles: any[] = [];
            errorsMeters.forEach(e => {
                subTitles.push({
                    titleTraductionKey: `meters[${e.index}]`,
                    subtitleTraductionKey: e.keys.join(', ')
                })
            });
            messages.push(
                {
                    titleTraductionKey: 'IotDevices.meter',
                    subTitles,
                    cssClassTitle: 'text-color-warn',
                })
        }
        if (arrayIsSet(errorsMachines) && controlMachine) {
            const subTitles: any[] = [];
            errorsMachines.forEach(e => {
                subTitles.push({
                    titleTraductionKey: `machines[${e.index}]`,
                    subtitleTraductionKey: e.keys.join(', ')
                })
            });
            messages.push(
                {
                    titleTraductionKey: 'IotDevices.machine',
                    subTitles,
                    cssClassTitle: 'text-color-warn',
                })
        }
        if (arrayIsSet(messages)) {
            return this.openDialogService.openSelectMultipleActions({
                title: this.ts.instant('alert.warningMessage'),
                messages,
                actions: [{
                    traductionKey: 'yes',
                    action: 'true',
                    color: 'primary',
                },
                {
                    traductionKey: 'no',
                    action: 'false',
                    color: 'accent',
                }],
                showCancelButton: false,
                cancelButtonLabel: 'cancel',
                confirmButtonLabel: 'confirm',
                multiple: false
            })
        } else { return of([{ action: 'true' }]) }
    }
    sendValue(): void {
        const edited = this.editedConfiguration();
        const original = this.originalConfiguration();
        const values: { [k: string]: any } = {};
        (['machines', 'meters'] as DeviceKey[]).forEach(key => {
            if (arrayIsSet(edited[key]) || arrayIsSet(original[key])) {
                values[key] = edited[key].map(item => this.getDeviceInfo(key, item).payload);
            }
        });
        values.connectionOptions = this.getDeviceInfo('connectionOptions', edited.connectionOptions).payload;
        if (!this.enableChanges || this.saving()) {
            return;
        }
        this.getErrorMessageDialog$(true, true).pipe(
            filter(value => value?.[0]?.action == 'true'),
            switchMap(() => {
                this.saving.set(true);
                this.dialogRef.disableClose = true;
                return this.data.save(values as IoTDeviceConfigurationObject).pipe(
                    finalize(() => {
                        this.saving.set(false);
                        this.dialogRef.disableClose = false;
                    })
                );
            })
        ).subscribe({
            next: () => this.dialogRef.close({ configuration: values }),
            error: error => this.alertService.error(error?.message ?? error),
        });
    }

    addDevice(key: DeviceKey): void {
        this.getErrorMessageDialog$(key == 'machines', key == 'meters').subscribe(value => {
            if (value == null || value.length == 0 || value[0].action != 'true') {
                return;
            }
            const device = key == 'machines' ? new MachineConfiguration() : new MeterConfiguration();
            this.editedConfiguration.update(config => ({ ...config, [key]: [...config[key], device] }));
            this.selectRow(key, device.id);
        })
    }

    cloneDevice(id: string | undefined): void {
        const config = this.editedConfiguration();
        const index = config.machines.findIndex(m => m.id == id);
        if (index < 0) {
            return;
        }
        const copy = MachineConfiguration.fromJson({ ...config.machines[index], id: new MachineConfiguration().id, element: undefined });
        const machines = [...config.machines];
        machines.splice(index + 1, 0, copy);
        this.editedConfiguration.set({ ...config, machines });
        this.selectRow('machines', copy.id);
    }

    deleteDevice(key: DeviceKey, id: string | undefined): void {
        const selected = this.selected();
        this.editedConfiguration.update(config => ({ ...config, [key]: config[key].filter(item => item.id != id) }));
        if (selected.kind == key && selected.id == id) {
            this.selectRow('connectionOptions');
        }
    }

    // form for a stored device: values via getPossibleValue, enabled fields/validators from comType
    private buildForm(key: keyForm, item: any): UntypedFormGroup {
        const objForm = key == 'connectionOptions' ? this.formObject.connectionOptions : this.getRowFormObject(key);
        const form = this.fb.group(objForm);
        if (item != null) {
            Object.keys(objForm).forEach(keyForm => {
                if (key == 'connectionOptions') {
                    if (keyForm in item) {
                        form.get(keyForm)?.setValue(item[keyForm], { emitEvent: false });
                    }
                } else {
                    const value = getPossibleValue(keyForm, this.configurationProperties[key], item);
                    if (value != null) {
                        form.get(keyForm)?.setValue(value, { emitEvent: false });
                    }
                }
            });
        }
        if (key != 'connectionOptions') {
            this.applyComTypeState(key, form);
        }
        form.updateValueAndValidity({ emitEvent: false });
        return form;
    }

    // synchronous version of the comType subscription in bindRowFormLogic
    private applyComTypeState(key: DeviceKey, form: UntypedFormGroup) {
        const comType = form.get('comType')?.value;
        const possibles = (key == 'machines' ? this.possiblesCompileMachines : this.possiblesCompileMeters) as { value: number, holdKeys: string[] }[];
        const selected = possibles.find(p => p.value === comType);
        if (selected == null || !arrayIsSet(selected.holdKeys)) {
            return;
        }
        possibles
            .filter(p => p.value != comType)
            .forEach(p => p.holdKeys
                .filter(k => !selected.holdKeys.includes(k))
                .forEach(k => form.get(k)?.disable({ emitEvent: false })));
        selected.holdKeys.forEach(k => {
            const control = form.get(k);
            if (control == null) {
                return;
            }
            control.enable({ emitEvent: false });
            if (comType == PossiblesCompileMachinesKeys.VIRTUAL && k == 'model') {
                control.clearValidators();
            } else {
                const validators = getValidator(getElementByKeyName(k, this.configurationProperties[key])!);
                if (validators != null) {
                    control.setValidators(validators as any);
                }
            }
            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    // form -> configuration, same mapping the old sendValue applied to every row
    private fromFormValues(key: keyForm, form: UntypedFormGroup): any {
        const value = form.value;
        if (key == 'connectionOptions') {
            const obj: { [k: string]: any } = {};
            Object.keys(value).forEach(subKey => {
                const element = getElementByKeyName(subKey, this.configurationProperties[key]);
                obj[subKey] = element?.getValueToSave ? element.getValueToSave(form.get(subKey)?.value) : form.get(subKey)?.value;
            });
            return obj;
        }
        let obj: any = {};
        Object.keys(value)
            .filter((k: any) => {
                if ((this.notSavedKeyForm as any)[key] != null) {
                    const keysNotSave = Object.keys((this.notSavedKeyForm as any)[key]).map(k => (this.notSavedKeyForm as any)[key][k].formControlName);
                    return !keysNotSave.includes(k);
                } else {
                    return true
                }
            })
            .forEach(subKey => {
                const element = getElementByKeyName(subKey, this.configurationProperties[key]);
                if (element?.getObjByValue != null) {
                    obj = element?.getObjByValue(value[subKey], obj);
                } else if (element?.substituionKeyNameInFormWithObj) {
                    obj = { ...obj, ...element?.substituionKeyNameInFormWithObj(value[subKey]) };
                } else {
                    obj[subKey] = getValueByElement(element, value[subKey]);
                }
            });
        return obj;
    }

    // cached by device reference: devices are replaced, never mutated
    private getDeviceInfo(key: keyForm, item: any): DeviceInfo {
        let info = this.deviceInfoCache.get(item);
        if (info == null) {
            const form = this.buildForm(key, item);
            const payload = this.fromFormValues(key, form);
            let invalidKeys: string[] | undefined;
            if (key == 'machines') {
                invalidKeys = MachineConfiguration.getInvalidKeys(form.value);
            } else if (key == 'meters') {
                invalidKeys = MeterConfiguration.getInvalidKeys(form.value);
            }
            info = {
                payload,
                serialized: this.normalizeFieldValue(payload),
                formRaw: form.getRawValue(),
                invalidKeys,
                hasInvalidControl: Object.keys(form.value).some(k => form.get(k)?.invalid),
            };
            this.deviceInfoCache.set(item, info);
        }
        return info;
    }

    private findDevice(configuration: EditableConfiguration, selected: SelectedDevice): any {
        if (selected.kind == 'connectionOptions') {
            return configuration.connectionOptions;
        }
        return configuration[selected.kind].find(item => item.id == selected.id);
    }

    public composeForm(detail: any, key: keyForm): void {
        const configurationData = this.configurationProperties[key].sort((aValue, bValue) => aValue.sortingValue - bValue.sortingValue);
        const valueFormHtml = this.getFormHtmlData(configurationData, detail, key);
        const form: UntypedFormGroup = valueFormHtml.formGruop;
        // tslint:disable-next-line:no-shadowed-variable
        const htmlData: htmlData[] = valueFormHtml.htmlData;
        const obj: { [k: string]: { form: UntypedFormGroup, htmlData: htmlData[] } } = {};
        obj[key] = { htmlData, form };
        this.htmlData[key] = htmlData;
        this.formObject[key] = form;
    }

    getFormHtmlData(configurationData: configurationElement[], detail: any, key: keyForm): {
        formGruop: any,
        htmlData: htmlData[],
        configElementSubForm: { element: configurationElement, htmlFormElement: htmlFormElement }[]
    } {
        const html: htmlData[] = [];
        const objForm: { [k: string]: any } = {};
        const configElementSubForm: any[] = [];
        const getValueDetail = (key: string, subKey: string | undefined = undefined) => {
            if (detail == null) {
                return undefined
            } else if (stringIsSet(subKey)) {
                return detail[key] != null ? detail[key][subKey] : undefined
            } else {
                return detail[key]
            }
        }
        configurationData.forEach(element => {
            if (element.showInForm) {
                let possibleValues;
                let valueForm;
                const keyName = element.keyName as keyof IotDevicesParse;
                if (element.typeForm == typeFormValue.GEO_POINT) {
                    valueForm = { lat: getValueDetail(keyName, 'latitude'), lng: getValueDetail(keyName, 'longitude') };
                } else if (element.typeForm == typeFormValue.ELENCO_WITH_FILTER) {
                    possibleValues = this.possibleValuesPointer[keyName]
                    if (arrayIsSet(this.possibleValuesPointer[keyName]) && detail[keyName] != null) {
                        const pValueCurrent = this.possibleValuesPointer[keyName].find(value => value.objectId === getValueDetail(keyName, 'objectId'))
                        valueForm = pValueCurrent;
                    }

                } else if (element.possibleValueIsInVariableComponent && this.data.possibleValuesSpecial && this.data.possibleValuesSpecial[key] && this.data.possibleValuesSpecial[key][keyName]) {
                    possibleValues = this.data.possibleValuesSpecial[key][keyName].map(s => {
                        const obj: dataForm = {
                            html: s.modelId,
                            valueForm: s.modelId,
                        }
                        return obj
                    })
                    valueForm = getValueDetail(keyName)
                } else if (isPointerConfigurationElement(element)) {
                    const pValueCurrent = this.getPossibleValuePointer(keyName, getValueDetail(keyName), key);
                    possibleValues = pValueCurrent.possibleValues;
                    valueForm = pValueCurrent.currentValue;
                } else if (arrayIsSet(element.possibleValues)) {
                    const castPossibleValues = element.possibleValues.map(pValue => {
                        return getValueByElement(element, pValue);
                    });
                    const pValueCurrent = this.getPossibleValuesCurrentValue(castPossibleValues, getValueDetail(keyName), element.traductionKey, element.possibleValuesTraduction);
                    possibleValues = pValueCurrent.possibleValues;
                    valueForm = pValueCurrent.currentValue;
                } else {
                    valueForm = getValueDetail(keyName);
                }

                // tslint:disable-next-line:no-shadowed-variable
                const htmlFormElement: htmlFormElement = {
                    type: element.typeForm,
                    possibleValues,
                    formControlName: element.keyName,
                    traduction: element.traductionKey,
                    formOptions: element.formOptions,
                    group: element.group,
                    formGroupChild: undefined
                };

                objForm[element.keyName] = [{
                    value: valueForm,
                    disabled: !element.editable
                },
                getValidator(element)
                ];
                // const subFormValue = this.getFormGroupChild(element, detail, detail[keyName])
                // if (isNotNullOrUndefined(subFormValue.formGroupChild)) {
                //     htmlFormElement.formGroupChild = subFormValue.formGroupChild as any;
                //     objForm[subFormValue.subObjForm?.key!] = subFormValue.subObjForm?.formGroup;
                //     configElementSubForm.push({element: element, htmlFormElement: htmlFormElement})
                // }
                this.addHtmlElement(element, htmlFormElement, html);
            }

        });
        return { formGruop: objForm, htmlData: html, configElementSubForm };
    }

    // tslint:disable-next-line:no-shadowed-variable
    private addHtmlElement(element: configurationElement, htmlFormElement: htmlFormElement, htmlData: htmlData[]): void {
        const key = element.section.toString();
        const tabGroupName = this.sectionNumberName.hasOwnProperty(key) ? this.sectionNumberName[key] : 'null';
        const index = htmlData.findIndex(htmlValue => htmlValue.tabGroupName === tabGroupName);
        if (index >= 0) {
            htmlData[index].forms.push(htmlFormElement);
        } else {
            htmlData.push({ tabGroupName, forms: [htmlFormElement] });
        }
    }

    private getPossibleValuesCurrentValue(possibleValues: string[], cValue: string, traductionKey: string, possibleValuesTraduction: any | undefined = undefined): {
        possibleValues: dataForm[] | undefined,
        currentValue: string | undefined
    } {
        let currentValue;
        if (arrayIsSet(possibleValues)) {
            const pValues = possibleValues.map((possibleValue, index) => {
                // tslint:disable-next-line:no-shadowed-variable
                const keyForm = possibleValue;
                if (possibleValue === cValue) {
                    currentValue = keyForm;
                }
                let traduction
                if (possibleValuesTraduction) {
                    traduction = possibleValuesTraduction(keyForm)
                } else if (keyForm.toString) {
                    traduction = keyForm.toString()
                } else {
                    traduction = keyForm
                }
                const obj = {
                    valueForm: keyForm,
                    html: keyForm,
                    traduction,
                };
                return obj;
            });
            const index = pValues.findIndex((value) => value.valueForm == cValue);
            if (index < 0 && cValue != null) {
                pValues.push({ valueForm: cValue, html: cValue, traduction: cValue });
                currentValue = cValue;
            }
            return { possibleValues: pValues, currentValue };
        } else {
            return { possibleValues: undefined, currentValue: cValue };
        }
    }

    private getPossibleValuePointer(keyname: string, currentValue: any, key: keyForm): {
        possibleValues: dataForm[] | undefined,
        currentValue: string | undefined
    } {
        if (arrayIsSet(this.getPossibleValuesPointerByKey(keyname))) {
            let cValue;

            const pValues = this.possibleValuesPointer[keyname].map(possibleValue => {
                if (currentValue != null && possibleValue.objectId === currentValue.objectId) {
                    cValue = possibleValue.objectId;
                }
                const obj: dataForm = {
                    valueForm: possibleValue.objectId,
                    html: possibleValue.objectId,
                    traduction: this.getTraduction(keyname, possibleValue, key)
                };
                return obj;
            });
            return { possibleValues: pValues, currentValue: cValue };
        } else {
            return { possibleValues: undefined, currentValue };
        }
    }

    getTraduction(keyName: string, object: any, key: keyForm) {
        const element = getElementByKeyName(keyName, this.configurationProperties[key]);
        if (element != null && isPointerConfigurationElement(element)) {
            return element.pointer?.getValue(object);
        } else {
            return object.objectId;
        }
    }

    // tslint:disable-next-line:no-shadowed-variable
    clearOrEnable(form: UntypedFormGroup, key: string, keyForm: keyForm): void {
        if (form.get(key)?.enabled) {
            form.get(key)?.reset();
        } else {
            this.configurationProperties[keyForm].forEach(el => {
                if (el.editable && el.showInForm && form.get(el.keyName) != null && form.get(el.keyName)?.disabled) {
                    form.get(el.keyName)?.enable();
                }
            });
        }
    }

    filterPredicateFunctionMachines(forms: htmlFormElement[], allValues: any, possibleCompileMeters: typeof PossiblesCompileMachines): htmlFormElement[] {
        const keySelected = allValues?.comType
        const index = possibleCompileMeters.findIndex(p => p.value == keySelected);
        let holdKeys = index >= 0 ? possibleCompileMeters[index].holdKeys : [];
        if (holdKeys && allValues && allValues.model == 'GPIO_IMPULSE_READ_GENERIC') {
            holdKeys = holdKeys.filter(k => k != 'functionMode')
        }
        if (arrayIsSet(holdKeys) && arrayIsSet(forms)) {
            return forms.filter(f => {
                return holdKeys.includes(f.formControlName)
            });
        } else {
            return []
        }
    }

    transformPossibleValuesElementMachines(form: htmlFormElement, formValues: [], currentValue: any, includeUsedValue = false): dataForm[] {
        let possibleValues: dataForm[] = []
        if (arrayIsSet(form.possibleValues)) {
            possibleValues = form.possibleValues;
        }
        if (form != null && arrayIsSet(formValues) && arrayIsSet(possibleValues) && form.formControlName == 'element' && !includeUsedValue) {
            const usedPossibleValues = formValues
                .filter((subForm: any) => subForm.element != null && currentValue != subForm.element)
                .map((subForm: any) => subForm.element)
            possibleValues = possibleValues.filter(p => !usedPossibleValues.includes(p.valueForm))
        }
        return possibleValues;
    }

    getUsedIdsPredicate(form: htmlFormElement, allValues: any, currentValue: string,) {
        if (form.formControlName === 'element' && arrayIsSet(allValues)) {
            const allElements = allValues
                .filter((v: any) => v.element != null && v.element != currentValue)
                .map((v: any) => v.element)
            if (arrayIsSet(allElements)) {
                return allElements
            }
        }
        return undefined
    }

    getPossiblevaluesPredicate(form: htmlFormElement, allValues: any, sensorDatasModel: KeyStringValue<SensorModelParse[]>,) {
        let pValues = form.possibleValues
        if (allValues) {
            if (allValues.model == 'GPIO_GENERIC') {
                if (form.formControlName == 'pin') {
                    pValues = BigBuddyPinWrapper.possiblePins.map(n => {
                        const obj: dataForm = {
                            html: n as any,
                            registerValue: n as any,
                            valueForm: n as any,
                            traduction: n as any,
                        }
                        return obj
                    })
                } else if (form.formControlName == 'input') {
                    const isInput = BigBuddyPinWrapper.getIsInput(allValues.pin)
                    if (isInput != null) {
                        pValues = form.possibleValues?.filter(value => isInput ? (value.valueForm == 'Entrata') : value.valueForm == 'Uscita')
                    } else {
                        pValues = form.possibleValues
                    }
                } else if (form.formControlName == 'functionMode') {
                    const functionModes = BigBuddyPinWrapper.getFunctionModeForPin(allValues.pin)
                    if (arrayIsSet(functionModes) != null) {
                        pValues = functionModes?.map(value => {
                            const obj: dataForm = {
                                html: value.value as any,
                                registerValue: value.value,
                                valueForm: value.value as any,
                                traduction: value.traduction
                            }
                            return obj
                        })
                    }
                }
            } else if (allValues.model == 'BUDDY_P') {
                if (form.formControlName == 'pin') {
                    pValues = PicoBuddyPinWrapper.possiblePins.map(n => {
                        const obj: dataForm = {
                            html: n as any,
                            registerValue: n as any,
                            valueForm: n as any,
                            traduction: n as any,
                        }
                        return obj
                    })
                } else if (form.formControlName == 'input') {
                    const isInput = PicoBuddyPinWrapper.getIsInput(allValues.pin)
                    if (isInput != null) {
                        pValues = form.possibleValues?.filter(value => isInput ? (value.valueForm == 'Entrata') : value.valueForm == 'Uscita')
                    } else {
                        pValues = form.possibleValues
                    }
                } else if (form.formControlName == 'functionMode') {
                    const functionModes = PicoBuddyPinWrapper.getFunctionModeForPin(allValues.pin)
                    if (arrayIsSet(functionModes) != null) {
                        pValues = functionModes?.map(value => {
                            const obj: dataForm = {
                                html: value.value as any,
                                registerValue: value.value,
                                valueForm: value.value as any,
                                traduction: value.traduction
                            }
                            return obj
                        })
                    }
                }
            } else if (allValues.model == 'GPIO_IMPULSE_READ_GENERIC') {
                if (form.formControlName == 'pin') {
                    pValues = [1, 2, 3, 4, 5, 6, 7].map(n => {
                        const obj: dataForm = {
                            html: n as any,
                            registerValue: n as any,
                            valueForm: n as any,
                            traduction: n as any,
                        }
                        return obj
                    })
                }
            }
        }
        if (form.formControlName == 'optionalParameters') {
            if (sensorDatasModel && sensorDatasModel.model) {
                const sensorModel = sensorDatasModel.model.find(s => s.modelId == allValues.model)
                pValues = sensorModel?.optionalParametersInfo as any
            }
        }
        return pValues
    }

    getTypeFormPredicate(form: htmlFormElement, allValues: any,) {
        let type = form.type
        if (allValues) {
            if (['GPIO_GENERIC', 'BUDDY_P', 'GPIO_IMPULSE_READ_GENERIC'].includes(allValues.model)) {
                if (form.formControlName == 'pin') {
                    type = typeFormValue.ELENCO_VINCOLATO
                } else if (form.formControlName == 'auxPin') {
                    type = typeFormValue.ELENCO_VINCOLATO
                }
            }
        }
        return type
    }

    getElementsPredicate(form: htmlFormElement, sensorDataType: SensorDataTypesParse[], elements: any[], lang: string = 'it'): ElementParse_v2[] | undefined {
        let pValues: any | undefined
        if (arrayIsSet(elements)) {
            if (form.formControlName == 'externalInfo') {
                pValues = elements
            } else if (form.formControlName == 'externalCommand') {
                pValues = elements
            }
        }
        return pValues
    }

    getSensorDataPredicate(form: htmlFormElement, sensorDataType: SensorDataTypesParse[], elements: any[], lang: string = 'it'): SensorDataTypesParse[] | undefined {
        let pValues: SensorDataTypesParse[] | undefined
        if (form.formControlName == 'externalInfo' && arrayIsSet(sensorDataType)) {
            pValues = sensorDataType
        }
        return pValues
    }


    get enableChanges(): boolean {
        return this.formFilter.get('enableChanges')?.value;
    }

    set enableChanges(value) {
        this.formFilter.get('enableChanges')?.setValue(value);
    }


    enableDisableChanges() {
        this.enableChanges = !this.enableChanges;
    }

    selectRow(kind: keyForm, id?: string) {
        this.selected.set({ kind, id });
        this.buildEditForm();
    }

    requestSelectRow(kind: keyForm, id?: string) {
        const selected = this.selected();
        if (selected.kind == kind && selected.id == id) {
            return;
        }
        this.selectRow(kind, id);
    }

    getSelectedIndex(): number {
        const selected = this.selected();
        return selected.kind == 'connectionOptions' ? -1 : this.editedConfiguration()[selected.kind].findIndex(item => item.id == selected.id);
    }

    private getRowFormObject(key: keyForm): any {
        if (this.notSavedKeyForm[key] == null) {
            return this.formObject[key];
        }
        const objForm = { ...this.formObject[key] };
        Object.keys(this.notSavedKeyForm[key] as any).forEach((k: string) => {
            const keyNotSave = (this.notSavedKeyForm[key] as any)[k].formControlName
            objForm[keyNotSave] = (this.notSavedKeyForm[key] as any)[k].defaultValue;
        });
        return objForm;
    }

    // model -> comType/optionalParameters and comType -> enabled fields/validators
    private bindRowFormLogic(key: keyForm, meterForm: UntypedFormGroup, valuesDefault: any): Subscription[] {
        const keyRadio = 'comType';
        const currentFormSubscriptions: Subscription[] = [];
        const sub_model = meterForm.get('model')?.valueChanges.pipe(
            pairwise(),
            filter(([prev, current]) => prev != current),
            map(([_, modelId]) => {
                const sensorModel = this.data.possibleValuesSpecial[key].model.find(s => s.modelId == modelId)
                return sensorModel
            }),
        )
            .subscribe(sensorModel => {
                if (sensorModel) {
                    const currentComType = meterForm.get(keyRadio)?.value
                    if (currentComType != sensorModel.comType) {
                        meterForm.get(keyRadio)?.setValue(sensorModel.comType)
                    }
                    const currentoOtionalParametersInfo = valuesDefault?.optionalParameters
                    let valueOptionalParametersInfo: any = {}
                    if (currentoOtionalParametersInfo != null) {
                        Object.keys(currentoOtionalParametersInfo)
                            .forEach((key: any) => {
                                valueOptionalParametersInfo[key] = currentoOtionalParametersInfo[key]
                            })
                    }
                    if (arrayIsSet(sensorModel.optionalParametersInfo)) {
                        sensorModel.optionalParametersInfo?.filter(o => {
                            return !Object.keys(valueOptionalParametersInfo).includes(o.key)
                        })
                            .forEach(o => valueOptionalParametersInfo[o.key] = undefined)
                    }
                    if (objectIsSet(valueOptionalParametersInfo)) {
                        meterForm.get('optionalParameters')?.setValue(valueOptionalParametersInfo)
                    } else {
                        meterForm.get('optionalParameters')?.reset()
                    }
                }
            }
            )
        if (sub_model != null) {
            currentFormSubscriptions.push(sub_model)
        }
        const sub = meterForm.get(keyRadio)?.valueChanges
            .subscribe(value => {
                const possiblesCompileMachines = key == "machines" ? this.possiblesCompileMachines : this.possiblesCompileMeters;
                const possibleValueMachines = possiblesCompileMachines.find(p => p.value === value);
                if (possibleValueMachines != null && arrayIsSet(possibleValueMachines.holdKeys)) {
                    const otherKeys = possiblesCompileMachines
                        .reduce((prev, current) => {
                            if (current.value != value) {
                                const holdKeysWithutCurrent = (current as any).holdKeys.filter((h: string) => {
                                    return !possibleValueMachines.holdKeys.includes(h)
                                });
                                prev = prev.concat(holdKeysWithutCurrent);
                            }
                            return prev;
                        }, []);
                    otherKeys.forEach(k => {
                        if (meterForm.get(k) != null) {
                            meterForm.get(k)?.disable();
                        }
                    })
                    setTimeout(() => {
                        possibleValueMachines.holdKeys.forEach(k => {
                            if (meterForm.get(k) != null) {
                                meterForm.get(k)?.enable();
                                if (meterForm.get(keyRadio)?.value == PossiblesCompileMachinesKeys.VIRTUAL && k == 'model') {
                                    meterForm.get(k)?.clearValidators();
                                    meterForm.get(k)?.updateValueAndValidity()
                                } else {
                                    const el = getElementByKeyName(k, this.configurationProperties[key])
                                    const validators = getValidator(el!)
                                    if (validators != null) {
                                        meterForm.get(k)?.setValidators(validators as any);
                                        meterForm.get(k)?.updateValueAndValidity()
                                    }
                                }
                            }

                        })
                    }, 20)
                }
            }
            )
        if (sub != null) {
            currentFormSubscriptions.push(sub)
        }
        return currentFormSubscriptions;
    }

    private buildEditForm() {
        this.editFormSubscriptions.forEach(s => s.unsubscribe());
        this.editFormSubscriptions = [];
        const selected = this.selected();
        const device = this.findDevice(this.editedConfiguration(), selected);
        if (device == null) {
            this.editForm = undefined;
            return;
        }
        const form = this.buildForm(selected.kind, device);
        if (selected.kind != 'connectionOptions') {
            this.editFormSubscriptions = this.bindRowFormLogic(selected.kind, form, device);
        }
        this.editFormSubscriptions.push(form.valueChanges.subscribe(() => this.writeEditForm(selected, form)));
        this.editForm = form;
    }

    private writeEditForm(selected: SelectedDevice, form: UntypedFormGroup) {
        const config = this.editedConfiguration();
        if (selected.kind == 'connectionOptions') {
            const next = { ...form.value };
            if (this.normalizeFieldValue(next) != this.normalizeFieldValue(config.connectionOptions)) {
                this.editedConfiguration.set({ ...config, connectionOptions: next });
            }
            return;
        }
        const kind = selected.kind;
        const current = config[kind].find(item => item.id == selected.id);
        if (current == null) {
            return;
        }
        const next = { id: selected.id, ...this.fromFormValues(kind, form) };
        if (this.normalizeFieldValue(next) != this.normalizeFieldValue(current)) {
            this.editedConfiguration.set({ ...config, [kind]: config[kind].map(item => item.id == selected.id ? next : item) });
        }
    }

    private normalizeFieldValue(value: any): string {
        return JSON.stringify(value ?? null, (k, v) => {
            if (v === undefined || v === '' || (Array.isArray(v) && v.length == 0)) {
                return null;
            }
            if (typeof v == 'number' || typeof v == 'boolean') {
                return String(v);
            }
            return v;
        });
    }

    isRowModified(kind: keyForm, id?: string): boolean {
        return this.modifiedIds().has(kind == 'connectionOptions' ? 'connectionOptions' : id!);
    }

    isFieldModified(controlName: string): boolean {
        const original = this.selectedOriginalFormValues();
        if (this.editForm == null || this.editForm.get(controlName) == null) {
            return false;
        }
        return this.normalizeFieldValue(this.editForm.get(controlName)?.value) != this.normalizeFieldValue(original?.[controlName]);
    }

    getSelectedKey(): keyForm {
        return this.selected().kind;
    }

    isNarrowLayout(): boolean {
        return window.innerWidth < 900;
    }

    // stored devices without the one being edited, so its element is not counted as used
    getOtherRowsPredicate(allValues: any[] | undefined, id: string | undefined): any[] | undefined {
        return allValues?.filter(item => item.id != id);
    }

    getRowSummaryPredicate(allValues: any[] | undefined, index: number, data: KeyStringValue<any>, key: keyForm): RowSummaryType {
        const values = arrayIsSet(allValues) ? allValues[index] : undefined;
        let element: any;
        if (values?.element != null && data?.possibleValuesPointer?.element) {
            element = data.possibleValuesPointer.element.find((el: ElementParse) => el.objectId == values.element);
        }
        const type = element?.type;
        const category = type?.category ?? type?.get?.('category');
        const knownCategory = Object.values(ElementTypeCategoryEnum).includes(category);
        return {
            name: element?.name,
            model: values?.model,
            protocolTraduction: values?.comType != null ? `IotDevices.comType.${key}.${values.comType}` : '',
            svgIcon: 'element-category-' + (knownCategory ? category : 'UNCATEGORIZED'),
        };
    }

    rowMatchesSearchPredicate(summary: RowSummaryType, search: string | undefined, protocol: string | undefined): boolean {
        if (!stringIsSet(search)) {
            return true;
        }
        const searchValue = search.trim().toLowerCase();
        return [summary?.name, summary?.model, protocol].some(v => v != null && v.toString().toLowerCase().includes(searchValue));
    }

    // groups the fields built from configurationElement by configurationElement.group
    getFormGroupsPredicate(forms: htmlFormElement[] | undefined, parameters: boolean): FormGroupHtmlType[] {
        const parametersGroups = ['optionalParameters', 'externalInfo', 'externalCommand'];
        const getIcon = (groupKey: string) => {
            switch (groupKey) {
                case 'general':
                    return 'dns';
                case 'energy':
                    return 'bolt';
                case 'optionalParameters':
                    return 'list';
                case 'externalInfo':
                    return 'info';
                case 'externalCommand':
                case 'system':
                    return 'settings';
                case 'mqttBroker':
                    return 'hub';
                case 'gps':
                    return 'location_on';
                case 'dataSubmission':
                    return 'cloud_upload';
                default:
                    return 'storage';
            }
        };
        const groups: FormGroupHtmlType[] = [];
        (forms ?? []).forEach(form => {
            const group = form.group ?? { key: 'general', traduction: 'IotDevices.groups.general' };
            if (parametersGroups.includes(group.key) != parameters) {
                return;
            }
            let current = groups.find(g => g.key == group.key);
            if (current == null) {
                current = { key: group.key, traduction: group.traduction, icon: getIcon(group.key), fields: [] };
                groups.push(current);
            }
            current.fields.push(form);
        });
        // without a comType no field passes the holdKeys filter, but Generale must still show the comType toggle
        if (!parameters && groups.length == 0) {
            groups.unshift({ key: 'general', traduction: 'IotDevices.groups.general', icon: getIcon('general'), fields: [] });
        }
        return groups;
    }

    getTrackFormGroup(index: number, group: FormGroupHtmlType): string {
        return group?.key ?? `group-${index}`;
    }

    getTitleMetersPredicate(allMetersValues: any[] | undefined, index: number, data: KeyStringValue<any>): TitleMetersType {
        let baudRate;
        let mqttTopics;
        let model;
        let modbusId;
        let elementId: string | undefined;
        if (arrayIsSet(allMetersValues) && allMetersValues[index] != null) {
            const values = allMetersValues[index]
            modbusId = values.modbusId
            baudRate = values.baudRate
            mqttTopics = values.mqttTopics
            if (window.innerWidth > 550) {
                model = values.model
                elementId = values.element
            }
        }
        let elementName;
        if (stringIsSet(elementId) && data && data.possibleValuesPointer && data.possibleValuesPointer.element) {
            const element = data.possibleValuesPointer.element.find((el: ElementParse) => el.objectId == elementId)
            if (element != null && stringIsSet(element.name)) {
                elementName = element.name;
            }
        }
        return { baudRate, model, modbusId, mqttTopics, elementName }
    }

    getTitleMachinesPredicate(allMetersValues: any[] | undefined, index: number, data: KeyStringValue<any>): TitleMachinesType {
        let otherValues: TitleMachinesType["otherValues"] = [];
        let elementId: string | undefined;
        let title: string | undefined;

        if (arrayIsSet(allMetersValues) && allMetersValues[index] != null) {
            const values = allMetersValues[index]
            title = 'IotDevices.comType.machines.' + values.comType
            elementId = values.element

            if (values.comType === PossiblesCompileMachinesKeys.HTTP) {
                if (window.innerWidth > 550) {
                    const keys = ['model',]
                    otherValues = keys.map(key => {
                        return { traduction: undefined, value: values[key] ? values[key] : '' }
                    })
                }
                otherValues.push({ traduction: undefined, value: values.baseUrl ? values.baseUrl : '' })
            } else if (values.comType === PossiblesCompileMachinesKeys.MODBUS_RTU) {
                let keys;
                if (window.innerWidth > 550) {
                    keys = ['baudRate', 'modbusId', 'modbusSerialPort']
                    otherValues = keys.map(key => {
                        return { traduction: undefined, value: values[key] ? values[key] : '' }
                    })
                } else {
                    keys = ['baudRate', 'modbusId']
                }
                otherValues = keys.map(key => {
                    return { traduction: 'IotDevices.' + key, value: values[key] ? values[key] : '' }
                })
            } else if (values.comType === PossiblesCompileMachinesKeys.MQTT) {
                const keys = ['mqttTopics',]
                otherValues = keys.map(key => {
                    return { traduction: undefined, value: values[key] ? values[key] : '' }
                })

            } else if (values.comType === PossiblesCompileMachinesKeys.Z_WAVE) {
                const keys = ['zwNodeId', 'zwSerialPort']
                otherValues = keys.map(key => {
                    return { traduction: 'IotDevices.' + key, value: values[key] ? values[key] : '' }
                })
            } else if (values.comType === PossiblesCompileMachinesKeys.GPIO) {
                const keys = ['pin']
                otherValues = keys.map(key => {
                    return { traduction: 'IotDevices.' + key, value: values[key] ? values[key] : '' }
                })
            }
            else if (values.comType === PossiblesCompileMachinesKeys.VIRTUAL) {
                const keys = ['model']
                otherValues = keys.map(key => {
                    return { traduction: 'IotDevices.' + key, value: values[key] ? values[key] : '' }
                })
            }
        }
        if (window.innerWidth < 550 && arrayIsSet(otherValues)) {
            elementId = undefined
        }
        if (stringIsSet(elementId) && data && data.possibleValuesPointer && data.possibleValuesPointer.element) {
            let elementName;
            const element = data.possibleValuesPointer.element.find((el: ElementParse) => el.objectId == elementId)
            if (element != null && stringIsSet(element.name)) {
                elementName = element.name;
            }
            otherValues?.push({ value: elementName })
        }
        return { title, otherValues }
    }

    getTitleConnectionOptionPredicate(connectionOption: any | undefined): TitleMachinesType {
        let title;
        let otherValues;
        if (connectionOption && connectionOption.zwSerialPort) {
            title = connectionOption.zwSerialPort;
        }
        if (connectionOption && connectionOption.mqttUsername) {
            otherValues = [{ value: connectionOption.mqttUsername }]
        }
        if (title == null && otherValues == null) {
            if (connectionOption && Object.keys(connectionOption).some(key => connectionOption[key] != null)) {
                otherValues = [{ traduction: 'IotDevices.yesConnectedOption', value: undefined }]
            } else {
                otherValues = [{ traduction: 'IotDevices.noConnectedOption', value: undefined }]
            }
        }
        return { title, otherValues }
    }

    formIsInError(errorsIndex: number[] | undefined, index: number) {
        if (arrayIsSet(errorsIndex)) {
            return errorsIndex.includes(index)
        } else {
            return false
        }
    }




}


// GPIO IMPULSE 
// PIN && INVERTED
//  possibleImpulseInputPins: number[] = [1, 2, 3, 4, 5, 6, 7];
// GPIO GENERIC pin function mode inverted 
// BUDDY_P
/**
 * FUNCTION MODE
 * PIN
 * INVERTED
 * static possiblePins: number[] = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 13, 14, 15, 16];
 * possibleFunctionModesPerPin: { [k: number]: number[] } = {
        1: [0],
        2: [0],
        3: [0],
        4: [0],
        5: [0, 1],
        6: [0, 1],
        7: [0, 1, 2],
        10: [0],
        11: [0],
        12: [0],
        13: [0],
        14: [0],
        15: [0],
        16: [1]
    };


    VIRTUAL

 */
//
